import { Account } from '@eagle/core-data-types';
import { Chip, styled, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { ComponentProps, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchAllCache, usePromise, useResponsiveText } from '../../hooks';
import { CacheDataTypes } from '../../types';
import { filterDeletedCache, innerText } from '../../util';
import { DynamicIcon, Icon } from '../icons';

interface StyledChipProps extends Omit<ComponentProps<typeof Chip>, 'sx'> {
  sx?: SxProps<Theme>;
}

const StyleChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'sx',
})<StyledChipProps>(({ theme, sx }) => ({
  '&.MuiChip-root': { minWidth: 90 },
  '& .MuiChip-label': { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(0) },
  ...(sx as Record<string, unknown>),
}));

export const SharedChip: FC<{ accountBinding: string[]; shared: boolean; deleted?: boolean; sx?: SxProps<Theme>; 'data-testid'?: string }> = ({ accountBinding, shared, deleted = false, sx, ...props }) => {
  const { t } = useTranslation(['common', 'manage']);
  const myAccountsCache = useFetchAllCache(CacheDataTypes.MY_ACCOUNT);

  const [accountIds] = usePromise(async () => {
    const data = await filterDeletedCache<Account>(myAccountsCache);
    return data.map(({ _id }) => _id);
  }, [myAccountsCache]);

  const filteredAccountBinding = useMemo(() => {
    if (!accountIds) return [];

    return [...new Set(accountBinding)].filter((id) => accountIds.includes(id));
  }, [accountBinding, accountIds]);

  const sharedText = t('common:common.labels.shared');
  const sharedStyle = useResponsiveText('0.8125rem', innerText(sharedText).length, {
    minFontSize: '0.625rem',
    maxFontSize: '0.8125rem',
    textLengthThreshold: 8,
  });

  if (!shared || !filteredAccountBinding.length) return <></>;

  return (
    <Tooltip
      arrow
      placement="top"
      title={deleted ? t('common:common.hint.deleted') : t('manage:page.person-types-list.shared-with.hint', { count: filteredAccountBinding.length })}
    >
      <StyleChip
        data-testid={props['data-testid']}
        label={<Typography style={sharedStyle}>{sharedText}</Typography>}
        disabled={deleted}
        icon={<DynamicIcon icon={<Icon icon="AccountTreeIcon" />} size={'16px'} />}
        sx={{
          '&.MuiChip-root': { minWidth: 110, pl: 1 },
          '& .MuiChip-label': { pl: 1, pr: 1 },
          ...sx,
        }}
      />
    </Tooltip>
  );
};
